import { html, css, LitElement } from 'lit-element';
import '@trumedianetworks/tmn-core-components/src/tmn-button';
import '@trumedianetworks/tmn-core-components/src/tmn-icon';
import '@trumedianetworks/tmn-core-components/src/tmn-select';
import '@trumedianetworks/tmn-core-components/src/tmn-form-group';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';
import axios from 'axios';
const searchUrl = '/v2/api/site-admin/shared-auth-search';

import { connect } from '../store/connect';
import { selectors } from '../site/';

class SharedAuthLookup extends LitElement {
    constructor() {
        super();

        this.fetchQueryDebounced = debounce(this._fetchQuery, 500);
        this.fetchQueryThrottled = throttle(this._fetchQuery, 500, { leading: false });
        this.handleSearchResponse = this.handleSearchResponse.bind(this);
        this.lookupData = [];
    }

    static get styles() {
        return [
            css`
                tmn-select {
                    font-size: 14px;
                    min-width: 155px;
                }
                .search-prompt {
                    font-size: 12px;
                    font-style: italics;
                    margin-bottom: 4px;
                }
                a.auth-site {
                    text-transform: capitalize;
                    user-select: none;
                    text-decoration: none;
                    /* flex:1; */
                    color: var(--routed-link-color);
                }
                a.auth-site:hover {
                    text-decoration: underline;
                }
                .action-container {
                    display: flex;
                    justify-content: space-between;
                    padding: 4px;
                }
                tmn-button {
                    display: flex;
                }
                tmn-select {
                    margin-bottom: 8px;
                }
                .filter-tooltip-icon {
                    opacity: 0.4;
                    padding: 6px;
                }
                .help-prompt {
                    margin-top: 8px;
                    display: flex;
                    align-items: center;
                    font-size: 12px;
                    font-style: italics;
                }
            `,
        ];
    }

    static get properties() {
        return {
            authSiteName: {
                type: String,
            },
            sharedAuthApiKey: {
                type: String,
            },
            showLookup: {
                type: Boolean,
            },
            searchValue: {
                type: String,
            },
            searchResults: {
                type: Array,
            },
            lookupData: {
                type: Array,
            },
            selectedSport: {
                type: String,
            },
            sportOptions: {
                type: Array,
            },
        };
    }

    firstUpdated() {
        // since we use this in a radio group, we need to keep our clicks here
        this.addEventListener('click', this.preventDefault);
    }

    updated(changedProps) {
        if (changedProps.has('sharedAuthApiKey')) {
            this.updateLookupData();
        }
    }

    render() {
        return html`${this.renderNameButton()}${this.renderLookup()}`;
    }

    renderNameButton() {
        if (this.showLookup) {
            return null;
        }
        const { authSiteName } = this;
        const buttonText = authSiteName || 'Lookup Auth Site';
        return html`<tmn-button @click=${this.toggleLookupMode}>
                <tmn-icon slot="startIcon" icon="sync_lock" iconVariant="outlined"></tmn-icon>
                ${buttonText}
            </tmn-button>
            <div class="help-prompt">
                <tmn-icon icon="info" class="filter-tooltip-icon"></tmn-icon>Search here to use a
                different site to authenticate users
            </div>`;
    }

    renderSports() {
        const { selectedSport, sportOptions } = this;
        const selection = sportOptions.filter((s) => s.name === selectedSport);
        return html`
            <div class="search-prompt">Search sport</div>
            <tmn-select
                @tmn-selections-changed=${this.changeSport}
                .selections=${selection}
                .data=${sportOptions}
                nosearch
                forcedefault></tmn-select>
        `;
    }

    renderLookup() {
        const { showLookup } = this;
        if (showLookup) {
            return html` ${this.renderSports()} ${this.renderSelectSharedAuth()} `;
        }
    }

    toggleLookupMode(e) {
        this.showLookup = !this.showLookup;
    }

    changeSport(e) {
        const { selections } = e.detail;
        this.selectedSport = selections[0].name;
    }

    preventDefault(e) {
        e.preventDefault();
        e.stopPropagation();
    }

    _fireRoutedItemEvent(e, data) {
        const event = new CustomEvent('shared-auth-changed', {
            detail: data,
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(event);
    }

    handleSearchInputChanged(e) {
        if (e.detail.searchValue) {
            this.searchValue = e.detail.searchValue;
            if (this.searchValue.length < 5) {
                this.fetchQueryThrottled();
            } else {
                this.fetchQueryDebounced();
            }
        }
    }

    _fetchQuery() {
        const { searchValue, selectedSport } = this;
        const sports = [selectedSport]; // for now single select but we could make multi
        const params = { q: searchValue, sports };
        const headers = {
            Accept: 'application/json',
        };
        axios
            .request({
                url: searchUrl,
                method: 'GET',
                headers,
                withCredentials: true,
                params,
            })
            .then(this.handleSearchResponse)
            .catch(this.handleSearchError);
    }

    handlesSelectionsChanged(e) {
        const [selection] = e.detail?.selections ?? [];
        if (selection) {
            const { name: sharedAuthApiKey, displayKey: authSiteName } = selection;
            this._fireRoutedItemEvent(e, { sharedAuthApiKey, authSiteName });
        }
        this.showLookup = false;
    }

    handleSearchResponse({ data }) {
        this.searchResults = data?.map((d) => ({ displayKey: d.name, name: d.sharedAuthApiKey }));
        this.updateLookupData();
    }

    updateLookupData() {
        const { sharedAuthApiKey, authSiteName, searchResults } = this;
        const newLookupData = searchResults ?? [];
        if (sharedAuthApiKey && authSiteName) {
            if (!newLookupData.some((d) => d.name === sharedAuthApiKey)) {
                newLookupData.push({ name: sharedAuthApiKey, displayKey: authSiteName });
            }
        }
        this.lookupData = newLookupData;
    }

    handleSearchError(e) {
        console.error(e);
    }

    renderSelectSharedAuth() {
        const selections = this.getSelections();
        const { searchValue, lookupData } = this;
        const prompt = 'Start typing to search';

        return html`
            <div class="search-prompt">${prompt}</div>
            <tmn-select
                @tmn-selections-changed=${this.handlesSelectionsChanged}
                @tmn-input-changed=${this.handleSearchInputChanged}
                ?multiple=${false}
                .value=${searchValue}
                .selections=${selections}
                .data=${lookupData}
                .filterOptionsBySearchValue=${false}></tmn-select>
        `;
    }

    getSelections() {
        const { sharedAuthApiKey, authSiteName } = this;
        if (sharedAuthApiKey && authSiteName) {
            return [{ name: sharedAuthApiKey, displayKey: authSiteName }];
        }
        return [];
    }
}

const mapStateToProps = (state, ownProps) => {
    return (state) => {
        return {
            sportOptions: selectors.siteSportOptionsSelector(state),
        };
    };
};

window.customElements.define('shared-auth-lookup', connect(mapStateToProps)(SharedAuthLookup));
